import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, User } from 'lucide-react';
import { siteData } from '../data/mock';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';

const EquipePage = () => {
  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Notre équipe — Nutritionnistes & thérapeutes Vevey & Clarens"
        description="Découvrez l'équipe pluridisciplinaire du Nutri Centre Riviera : nutritionnistes spécialisées en santé féminine, hormonale, SOPK et thérapie émotionnelle. Vevey, Clarens, Montreux."
        keywords="équipe nutritionniste Vevey, thérapeute Clarens, spécialiste SOPK Riviera, nutritionniste femme Montreux"
        url="https://nutricentreriviera.ch/equipe"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          "@id": "https://nutricentreriviera.ch/equipe#zara-nawid",
          "name": "Zara Nawid",
          "honorificPrefix": "Mme",
          "jobTitle": "Nutritionniste diplômée — Thérapeute en nutrition MCO",
          "description": "Nutritionniste diplômée, thérapeute en nutrition MCO, agréée ASCA et RME. Spécialiste du SOPK, des hormones féminines et de la perte de poids durable. Parcours universitaire en biologie et plus de 10 ans d'expérience dans le domaine médical.",
          "image": "https://nutricentreriviera.ch/images/zara-nawid.webp",
          "url": "https://nutricentreriviera.ch/equipe",
          "telephone": "+41 76 779 93 02",
          "email": "nutririviera@gmail.com",
          "worksFor": {
            "@type": "MedicalBusiness",
            "@id": "https://nutricentreriviera.ch/#localbusiness",
            "name": "Harmonie Féminine par Nutri Centre Riviera"
          },
          "workLocation": [
            {
              "@type": "Place",
              "name": "Cabinet de Vevey",
              "address": { "@type": "PostalAddress", "streetAddress": "Av. du Général-Guisan 26", "addressLocality": "Vevey", "postalCode": "1800", "addressRegion": "VD", "addressCountry": "CH" }
            },
            {
              "@type": "Place",
              "name": "Cabinet de Clarens",
              "address": { "@type": "PostalAddress", "streetAddress": "Rue du Grammont 2", "addressLocality": "Clarens", "postalCode": "1815", "addressRegion": "VD", "addressCountry": "CH" }
            }
          ],
          "memberOf": [
            { "@type": "Organization", "name": "ASCA — Fondation suisse pour les médecines complémentaires" },
            { "@type": "Organization", "name": "RME — Registre de Médecine Empirique" }
          ],
          "knowsAbout": [
            "SOPK (syndrome des ovaires polykystiques)",
            "Hormones féminines",
            "Perte de poids durable",
            "Préménopause et ménopause",
            "Résistance à l'insuline",
            "Nutrition métabolique",
            "Rééquilibrage alimentaire"
          ],
          "knowsLanguage": ["fr"],
          "sameAs": ["https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera"]
        }}
      />
      {/* Hero Section */}
      <PageHero
        image="/images/equipe-hero.webp"
        alt="Espace d'accueil premium du Nutri Centre Riviera — fauteuils crème et mur vert sauge, ambiance chaleureuse"
        title="Notre Équipe"
        subtitle="Des professionnels passionnés et qualifiés à votre service"
      />

      {/* Team Section */}
      <section className="section-lg" style={{ background: 'white' }}>
        <div className="container">
          <div style={{
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto 4rem'
          }}>
            <p className="body-large">
              Notre équipe pluridisciplinaire associe expertise médicale, 
              accompagnement nutritionnel et soutien émotionnel pour une prise en charge globale.
            </p>
          </div>

          <div className="team-grid">
            {/* Ordre: Nutritionniste (avec photo) → Cardiologue → Diététicienne */}
            {[1, 3, 2].map((memberId) => {
              const member = siteData.team.find(m => m.id === memberId);
              if (!member) return null;

              return (
                <div
                  key={member.id}
                  style={{
                    background: 'var(--bg-card)',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    border: '1px solid var(--border-light)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  className="team-card"
                >
                {/* Photo réelle ou placeholder discret pour profils à définir */}
                {member.image ? (
                  <div style={{
                    height: '300px',
                    overflow: 'hidden',
                    background: '#f5f5f5'
                  }}>
                    <img 
                      src={member.image}
                      alt={member.name}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
          decoding="async"/>
                  </div>
                ) : (
                  <div style={{
                    height: '300px',
                    background: 'linear-gradient(135deg, #f8fdf9 0%, #E9EEE8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                    textAlign: 'center'
                  }}>
                    <div>
                      <div style={{
                        width: '88px',
                        height: '88px',
                        borderRadius: '50%',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1.25rem',
                        boxShadow: '0 4px 14px rgba(61, 84, 70, 0.15)'
                      }}>
                        <User size={36} strokeWidth={1.5} style={{ color: 'var(--accent-primary)' }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Content avec hauteur fixe pour harmonisation */}
                <div style={{ 
                  padding: '2rem',
                  minHeight: '380px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {member.name && (
                    <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                      {member.name}
                    </h3>
                  )}
                  <div className="caption" style={{ 
                    color: 'var(--accent-primary)', 
                    marginBottom: '0.75rem',
                    marginTop: member.name ? 0 : '0.25rem'
                  }}>
                    {member.role}
                  </div>
                  <p className="body-small" style={{ 
                    fontWeight: 500, 
                    marginBottom: '1rem',
                    color: 'var(--text-primary)'
                  }}>
                    {member.speciality}
                  </p>
                  <p className="body-medium" style={{ flex: 1 }}>
                    {member.description}
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      marginTop: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: 'var(--accent-primary)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      padding: 0,
                      transition: 'gap 0.2s ease',
                      textDecoration: 'none'
                    }}
                    className="contact-link"
                  >
                    <Mail size={18} />
                    <span>Contacter</span>
                  </Link>
                </div>
              </div>
            );
            })}
          </div>

          <style>{`
            .team-card:hover {
              transform: translateY(-6px);
              box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
              border-color: var(--accent-primary);
            }
            .contact-link:hover {
              gap: 0.75rem !important;
            }
          `}</style>
        </div>
      </section>

      {/* Join Team Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '3rem',
            textAlign: 'center',
            border: '1px solid var(--border-light)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
              Rejoignez notre équipe
            </h2>
            <p className="body-large" style={{ marginBottom: '2rem' }}>
              Le centre a pour vocation de se développer. 
              Si vous êtes un professionnel de santé passionné par l'approche pluridisciplinaire, 
              contactez-nous.
            </p>
            <Link to="/contact" className="btn-primary">
              Nous contacter
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'var(--accent-primary)',
        color: 'white',
        padding: '5rem 0'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            Prenez rendez-vous avec notre équipe
          </h2>
          <p className="body-large" style={{ 
            color: 'white', 
            opacity: 0.95, 
            maxWidth: '600px', 
            margin: '0 auto 2.5rem' 
          }}>
            Bénéficiez d'un accompagnement personnalisé par nos professionnels
          </p>
          <a
            href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary" 
            style={{ 
              background: 'white', 
              color: 'var(--accent-primary)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              textDecoration: 'none'
            }}
          >
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default EquipePage;
